import * as React from "react";
import { motion, useTransform } from "motion/react";
import { Crosshair } from "lucide-react";
import { BrandLoader, Button, cn } from "@eduviaos/ui-kit";
import type { CourseLeaf, CourseNavigation } from "../../../lib/learning/types";
import type { ZiggyAge } from "../../../lib/ziggy";
import { useLearnLocale } from "../../../lib/i18n/useLearnLocale";
import { RoadmapCoachStrip } from "./RoadmapCoachStrip";
import { RoadmapStepList } from "./RoadmapStepList";
import { courseNavigationToRoadmap, resolveInitialStepIndex } from "./normalize";
import type { Roadmap3DProps, RoadmapStep } from "./types";
import { useRoadmapScroll } from "./useRoadmapScroll";
import { buildWorldNodes } from "./world";

const RoadmapScene = React.lazy(() => import("./scene/RoadmapScene"));

/** three.js is ~600KB, so it only loads once a learner opens a roadmap. */
function supportsWebGL(): boolean {
  if (typeof document === "undefined") return false;
  try {
    const canvas = document.createElement("canvas");
    return Boolean(
      canvas.getContext("webgl2") ??
      canvas.getContext("webgl") ??
      canvas.getContext("experimental-webgl"),
    );
  } catch {
    return false;
  }
}

/**
 * Scroll-driven WebGL roadmap. Scrolling drives a camera down a real 3D road:
 * checkpoints are objects standing on the verge that grow as you approach and
 * sweep past behind you, with the finish arch waiting at the end.
 *
 * `useScroll` only mounts with the WebGL track. The 2D fallback never owns that
 * ref, so Motion does not warn about an unhydrated target.
 */
export function Roadmap3D(props: Roadmap3DProps) {
  const { strings } = useLearnLocale();
  const [webgl] = React.useState(supportsWebGL);
  const nodeCount = React.useMemo(
    () => buildWorldNodes(props.roadmap.steps).length,
    [props.roadmap.steps],
  );

  if (nodeCount === 0) {
    return (
      <div className="border-border/60 text-muted-foreground rounded-2xl border p-8 text-center text-sm">
        {strings.roadmapNoSteps}
      </div>
    );
  }

  if (!webgl) {
    const resumeStepIndex = props.initialStepIndex ?? resolveInitialStepIndex(props.roadmap.steps);
    return (
      <div className="space-y-3">
        <p className="text-muted-foreground text-xs">{strings.roadmapListFallback}</p>
        <RoadmapStepList
          steps={props.roadmap.steps}
          focusStepIndex={resumeStepIndex}
          busyStepId={props.busyStepId ?? null}
          onStepAction={props.onStepAction}
        />
      </div>
    );
  }

  return <Roadmap3DScroll {...props} />;
}

function Roadmap3DScroll({
  roadmap,
  initialStepIndex,
  showProgress = true,
  busyStepId = null,
  ziggyAge = "grown",
  onStepChange,
  onProgressChange,
  onStepAction,
}: Roadmap3DProps) {
  const { strings } = useLearnLocale();
  const scrollTrackRef = React.useRef<HTMLDivElement>(null);
  const sceneRef = React.useRef<HTMLDivElement>(null);
  const [frozen, setFrozen] = React.useState(false);

  const {
    nodes,
    driveRef,
    scrollYProgress,
    scrollHeightVh,
    focusNodeIdx,
    focusStepIndex,
    setFocusNodeIdx,
    resumeStepIndex,
    jumpToStepIndex,
    reduced,
  } = useRoadmapScroll({
    roadmap,
    scrollTrackRef,
    initialStepIndex,
    onStepChange,
    onProgressChange,
  });

  const focusStep = roadmap.steps[focusStepIndex] ?? null;
  const checkpoints = roadmap.steps.filter((s) => s.kind === "checkpoint");
  const completedCount = checkpoints.filter((s) => s.status === "completed").length;
  const percentRef = React.useRef<HTMLSpanElement>(null);
  const progressScale = useTransform(scrollYProgress, (v) => Math.max(v, 0.004));

  // The HUD number tracks scroll without re-rendering React on every frame.
  React.useEffect(() => {
    const write = (v: number) => {
      if (percentRef.current) percentRef.current.textContent = `${Math.round(v * 100)}%`;
    };
    write(scrollYProgress.get());
    return scrollYProgress.on("change", write);
  }, [scrollYProgress]);

  // Stop rendering frames while the scene is scrolled out of view.
  React.useEffect(() => {
    const el = sceneRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setFrozen(!(entry?.isIntersecting ?? true)),
      { rootMargin: "10% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const onKeyDown = (event: React.KeyboardEvent) => {
    const step = (delta: number) => {
      event.preventDefault();
      const next = nodes[Math.min(Math.max(focusNodeIdx + delta, 0), nodes.length - 1)];
      if (next) jumpToStepIndex(next.index);
    };
    if (event.key === "ArrowDown" || event.key === "ArrowRight") step(1);
    else if (event.key === "ArrowUp" || event.key === "ArrowLeft") step(-1);
    else if (event.key === "PageDown") step(4);
    else if (event.key === "PageUp") step(-4);
    else if (event.key === "Home" && nodes[0]) jumpToStepIndex(nodes[0].index);
    else if (event.key === "End" && nodes.length) jumpToStepIndex(nodes[nodes.length - 1]!.index);
  };

  return (
    <section
      ref={scrollTrackRef}
      className="relative"
      style={{ height: `${scrollHeightVh}vh` }}
      aria-label={strings.roadmapScrollAria(roadmap.title)}
    >
      <div
        ref={sceneRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        role="group"
        aria-roledescription={strings.roadmapRoleDescription}
        aria-label={strings.roadmapKeyboardHint}
        className={cn(
          "border-border/60 focus-visible:ring-primary/60 sticky z-10 overflow-hidden rounded-2xl border shadow-[var(--shadow-card)] focus:outline-none focus-visible:ring-2",
          // Parks below the mobile header, which is itself sticky and would
          // otherwise cover the progress bar and the jump-to-current control.
          "top-[3.9rem] lg:top-0",
          "h-[min(72dvh,46rem)] sm:h-[min(88dvh,52rem)]",
        )}
      >
        <React.Suspense
          fallback={
            <div className="flex h-full items-center justify-center bg-gradient-to-b from-sky-200 to-amber-50">
              <BrandLoader size={44} label="Loading your roadmap" />
            </div>
          }
        >
          <RoadmapScene
            nodes={nodes}
            driveRef={driveRef}
            focusNodeIdx={focusNodeIdx}
            focusStepIndex={focusStepIndex}
            busyStepId={busyStepId}
            reduced={reduced}
            frozen={frozen}
            onFocusNode={setFocusNodeIdx}
            onStepAction={onStepAction}
          />
        </React.Suspense>

        {/* Vignette. A postprocessing pass would cost another chunk for one
            gradient, and this sits above the canvas without touching the
            frameloop: corners settle, the eye stays on the road. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-20"
          style={{
            background:
              "radial-gradient(125% 95% at 50% 42%, transparent 58%, rgba(38,32,22,0.07) 84%, rgba(30,26,18,0.17) 100%)",
          }}
        />

        {showProgress && (
          <div className="pointer-events-none absolute inset-x-3 top-3 z-30 flex items-center gap-2 sm:inset-x-4">
            <div className="bg-background/40 h-1.5 flex-1 overflow-hidden rounded-full backdrop-blur-sm">
              <motion.div
                className="bg-primary h-full origin-left rounded-full"
                style={{ scaleX: progressScale }}
              />
            </div>
            <span className="bg-background/85 rounded-full px-2 py-0.5 text-[0.62rem] font-semibold tabular-nums shadow-sm backdrop-blur-sm sm:text-xs">
              {completedCount}/{checkpoints.length} ·{" "}
              <span ref={percentRef} aria-live="off">
                0%
              </span>
            </span>
          </div>
        )}

        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => jumpToStepIndex(resumeStepIndex)}
          className="absolute right-3 top-10 z-30 h-7 gap-1 rounded-full px-2.5 text-[0.65rem] font-semibold shadow-md transition-transform duration-150 active:scale-[0.97] sm:right-4 sm:top-12"
        >
          <Crosshair className="size-3" aria-hidden />
          {strings.roadmapCurrentStep}
        </Button>

        <div aria-live="polite" className="sr-only">
          {focusStep ? strings.roadmapCheckpointAnnounce(focusStep.title) : ""}
        </div>

        <RoadmapCoachStrip focusStep={focusStep} ziggyAge={ziggyAge} />
      </div>
    </section>
  );
}

export function Roadmap3DFromNavigation({
  navigation,
  busyLeafId,
  ziggyAge,
  onQualifyStep,
}: {
  navigation: CourseNavigation;
  busyLeafId?: string | null;
  ziggyAge?: ZiggyAge;
  onQualifyStep: (leaf: CourseLeaf) => void;
}) {
  const roadmap = React.useMemo(() => courseNavigationToRoadmap(navigation), [navigation]);

  return (
    <Roadmap3D
      roadmap={roadmap}
      busyStepId={busyLeafId ?? null}
      ziggyAge={ziggyAge}
      onStepAction={(step: RoadmapStep) => {
        const leaf = step.meta?.leaf as CourseLeaf | undefined;
        if (leaf) onQualifyStep(leaf);
      }}
    />
  );
}
